import { awaitJSON } from '../../utils/fetch-utils';
import {
  GenerationDialogState,
  GenerationDialogStateType,
  PRESENTATION_STATUS_POLL_INTERVAL_MS,
  PRESENTATION_STATUS_POLL_LIMIT_MS,
  PresentationStatus,
  SlideJobErrorCode,
  SlideJobStatus,
  SlideJobStatusType
} from './presentation-status-constants';

export const getJobIdFromResponse = (body: unknown): string | undefined => {
  if (typeof body !== 'object' || body === null || !('jobId' in body)) return undefined;
  const { jobId } = body;
  return typeof jobId === 'string' && jobId.length > 0 ? jobId : undefined;
};

// Reads the jobId from a generatePresentation response. The export already started, so a body
// that does not parse gives no jobId instead of an error.
export const readJobIdFromResponse = async (response: {
  text(): Promise<string>;
}): Promise<string | undefined> => {
  try {
    return getJobIdFromResponse(await awaitJSON<unknown>(response));
  } catch {
    return undefined;
  }
};

export const isFinalSlideJobStatus = (status: SlideJobStatusType): boolean =>
  status === SlideJobStatus.SUCCEEDED || status === SlideJobStatus.FAILED;

// lastResponseAt is the time of the last response, successful or failed (0 before the first one).
export const hasPollLimitPassed = (pollStartedAt: number, lastResponseAt: number): boolean =>
  lastResponseAt - pollStartedAt >= PRESENTATION_STATUS_POLL_LIMIT_MS;

interface RefetchIntervalArgs {
  status: PresentationStatus | undefined;
  pollLimitPassed: boolean;
}

// refetchInterval for the status query: poll until a final status or the limit. A failed request
// does not stop polling, so one gateway timeout or cold start does not hide the result.
export const getPresentationStatusRefetchInterval = ({
  status,
  pollLimitPassed
}: RefetchIntervalArgs): number | false => {
  if (pollLimitPassed) return false;
  if (status && isFinalSlideJobStatus(status.status)) return false;
  return PRESENTATION_STATUS_POLL_INTERVAL_MS;
};

interface GenerationDialogStateArgs {
  jobId: string | undefined;
  status: PresentationStatus | undefined;
  hasError: boolean;
  pollLimitPassed: boolean;
}

export const getGenerationDialogState = ({
  jobId,
  status,
  hasError,
  pollLimitPassed
}: GenerationDialogStateArgs): GenerationDialogStateType => {
  if (!jobId) return GenerationDialogState.NO_JOB_ID;
  if (status?.status === SlideJobStatus.SUCCEEDED) return GenerationDialogState.SUCCEEDED;
  if (status?.status === SlideJobStatus.FAILED) {
    return status.errorCode === SlideJobErrorCode.WORKER_TIMEOUT
      ? GenerationDialogState.EXPIRED
      : GenerationDialogState.FAILED;
  }
  if (pollLimitPassed) return GenerationDialogState.UNKNOWN;
  // After a failed request, keep the last status while polling continues.
  if (status?.status === SlideJobStatus.RUNNING) return GenerationDialogState.RUNNING;
  if (status?.status === SlideJobStatus.QUEUED) return GenerationDialogState.QUEUED;
  if (hasError) return GenerationDialogState.UNKNOWN;
  // The endpoint creates the job as queued, so no status yet also means queued.
  return GenerationDialogState.QUEUED;
};
